import { Box, Typography, TextField, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useEffect, useMemo } from 'react';
import {
  extractAtTokens,
  mergeParamsForTokens,
  stripMustache,
  toMustache,
} from '../../Utilities/DocumentPdfTemplateUtils';

export function DocumentPdfNodeFields({ localNodeData, updateNodeData }) {
  const bodyText = localNodeData.BodyText || '';
  const apiBody = localNodeData.APIBody || '';

  const tokens = useMemo(() => extractAtTokens(bodyText, apiBody), [bodyText, apiBody]);

  // Mantener Params sincronizado con tokens detectados (sin borrar valores ya mapeados)
  useEffect(() => {
    const merged = mergeParamsForTokens(tokens, localNodeData.Params);

    // Evitar loops de render: solo update si cambia (comparación simple)
    const prev = Array.isArray(localNodeData.Params) ? localNodeData.Params : null;
    const same =
      prev &&
      prev.length === merged.length &&
      prev.every((p, i) => p?.name === merged[i].name && p?.value === merged[i].value);

    if (!same) {
      updateNodeData('Params', merged);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens]);

  const params = useMemo(() => mergeParamsForTokens(tokens, localNodeData.Params), [tokens, localNodeData.Params]);

  const outputKey = localNodeData.OutputKey || '';
  const outputKeyError = !outputKey.trim();

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
          OutputKey (requerido)
        </Typography>
        <TextField
          value={outputKey}
          size="small"
          fullWidth
          error={outputKeyError}
          helperText={
            outputKeyError
              ? 'OutputKey es obligatorio. El PDF en base64 queda guardado en vars[outputKey]'
              : 'El PDF en base64 queda guardado en vars[outputKey]'
          }
          onChange={(e) => updateNodeData('OutputKey', e.target.value)}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
          HTML template (BodyText)
        </Typography>
        <TextField
          value={bodyText}
          multiline
          rows={8}
          fullWidth
          placeholder="<html>... usa tokens tipo @nombre ...</html>"
          onChange={(e) => updateNodeData('BodyText', e.target.value)}
        />
        <Typography variant="caption" color="text.secondary">
          Variables detectadas: {tokens.length}
        </Typography>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
          Fallback APIBody (opcional)
        </Typography>
        <TextField
          value={apiBody}
          multiline
          rows={4}
          fullWidth
          placeholder="(Opcional)"
          onChange={(e) => updateNodeData('APIBody', e.target.value)}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
          Mapeo de variables del template
        </Typography>

        {tokens.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Pega el HTML para detectar tokens tipo @nombre.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Token</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Variable del flow</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tokens.map((token, idx) => {
                const p = params[idx] || { name: token.slice(1), value: '' };
                return (
                  <TableRow key={token}>
                    <TableCell sx={{ width: 180, fontFamily: 'monospace' }}>{token}</TableCell>
                    <TableCell>
                      <TextField
                        value={toMustache(p.value)}
                        size="small"
                        fullWidth
                        placeholder="{{varFlow}}"
                        onChange={(e) => {
                          const clean = stripMustache(e.target.value);
                          const next = params.map((row, i) =>
                            i === idx
                              ? { name: row.name, value: clean }
                              : { name: row.name, value: row.value }
                          );
                          updateNodeData('Params', next);
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Se guarda como Params: {'{ name: tokenSin@, value: varFlowSinLlaves }'}.
        </Typography>
      </Box>
    </>
  );
}
